import React, { useState } from "react";
import { MapPin, Phone, Mail, Instagram, Send } from "lucide-react";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.firstName || !formData.message) {
      alert("Please fill in your name and message");
      return;
    }

    // Format the WhatsApp message
    const whatsappMessage = `
*New Contact Form Message*

*Name:* ${formData.firstName} ${formData.lastName}
${formData.email ? `*Email:* ${formData.email}` : ""}
${formData.phone ? `*Phone:* ${formData.phone}` : ""}
*Subject:* ${formData.subject}

*Message:*
${formData.message}
    `.trim();

    // Your WhatsApp number (remove spaces and special characters)
    const whatsappNumber = "96179498090"; // +961 79 498 090

    // Create WhatsApp URL with pre-filled message
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      whatsappMessage
    )}`;

    // Open WhatsApp
    window.open(whatsappURL, "_blank");

    // Optional: Clear form after sending
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      subject: "General Inquiry",
      message: "",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f3f9] to-[#e8e4f3] py-12">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h1 className="mb-4 font-serif text-5xl text-gray-900">
            Get In Touch
          </h1>
          <p className="text-lg text-gray-600">
            We'd love to hear from you. Our team is here to help.
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-2">
          <div className="p-8 bg-white shadow-xl rounded-3xl lg:p-12">
            <h2 className="mb-6 text-2xl font-semibold text-gray-900">
              Send us a message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                  placeholder="john.doe@example.com"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                  placeholder="+961 XX XXX XXX"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Subject
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                >
                  <option>General Inquiry</option>
                  <option>Order Status</option>
                  <option>Product Question</option>
                  <option>Custom Design</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#1a1a3e] to-[#2a2a5e] text-white py-4 rounded-full font-semibold hover:from-[#2a2a5e] hover:to-[#1a1a3e] transition shadow-xl flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                Send via WhatsApp
              </button>
              <p className="text-xs text-center text-gray-500">
                This will open WhatsApp with your message pre-filled
              </p>
            </form>
          </div>

          <div className="space-y-8">
            <div className="p-8 bg-white shadow-xl rounded-3xl">
              <h2 className="mb-6 text-2xl font-semibold text-gray-900">
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#e8e4f3] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#d4af37]" />
                  </div>
                  <div>
                    <h3 className="mb-1 font-semibold text-gray-900">
                      Online Store
                    </h3>
                    <p className="text-gray-600">
                      Tripoli
                      <br />
                      North Lebanon
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#e8e4f3] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#d4af37]" />
                  </div>
                  <div>
                    <h3 className="mb-1 font-semibold text-gray-900">
                      Call/WhatsApp Us
                    </h3>
                    <a
                      href="https://wa.me/96179498090"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#d4af37] transition"
                    >
                      +961 79 498 090
                    </a>
                    <p className="mt-1 text-sm text-gray-500">
                      Mon-Sat: 9AM-6PM
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#e8e4f3] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#d4af37]" />
                  </div>
                  <div>
                    <h3 className="mb-1 font-semibold text-gray-900">
                      Email Us
                    </h3>
                    <a
                      href="mailto:maisondebijoux.lb@gmail.com"
                      className="text-gray-600 hover:text-[#d4af37] transition"
                    >
                      maisondebijoux.lb@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#0f0f2e] to-[#2a1a4e] rounded-3xl shadow-xl p-8 text-white">
              <h3 className="mb-4 text-xl font-semibold">Business Hours</h3>
              <div className="space-y-2 text-gray-300">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span className="font-semibold">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span className="font-semibold">10:00 AM - 5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="font-semibold">Closed</span>
                </div>
              </div>
            </div>

            <div className="p-8 bg-white shadow-xl rounded-3xl">
              <h3 className="mb-4 text-xl font-semibold text-gray-900">
                Follow Us
              </h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/maison_bijouxx?igsh=aWx4bWNsZ2lydW44"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-12 h-12 transition bg-gray-100 rounded-full hover:bg-amber-100"
                >
                  <Instagram className="w-6 h-6 text-gray-600" />
                </a>
              </div>
            </div>

            {/* Quick WhatsApp Button */}
            <a
              href="https://wa.me/96179498090"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 transition shadow-xl bg-gradient-to-r from-green-500 to-green-600 rounded-3xl hover:from-green-600 hover:to-green-700 group"
            >
              <div className="flex items-center gap-4 text-white">
                <div className="flex items-center justify-center rounded-full w-14 h-14 bg-white/20">
                  <Phone className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="mb-1 text-xl font-semibold">Quick WhatsApp</h3>
                  <p className="text-sm text-green-50">
                    Chat with us instantly
                  </p>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
